import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

// src/pages/Reports/IssuanceReporting/shared/formatters.ts
// README.md mục 7 (mục 19-20, đặc tả báo cáo phát hành vé số) — quy ước hiển thị dùng CHUNG cho
// toàn bộ module IssuanceReporting: ngày giờ dd/MM/yyyy HH:mm:ss, ngày dd/MM/yyyy, số có phân
// tách hàng nghìn, tỷ lệ %. Các trang Reports/* cũ tự lặp lại toLocaleString ở mỗi file — ở đây
// gom 1 chỗ vì module này có tới 18 báo cáo dùng chung y hệt định dạng, tránh lặp 18 lần.

/** Số lượng (vé, dải...) — phân tách hàng nghìn theo vi-VN, "0" nếu null/undefined. */
export const formatQty = (value?: number | null): string =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

/** Giá trị tiền — phân tách hàng nghìn + hậu tố "đ", "-" nếu null/undefined. */
export const formatCurrency = (value?: number | null): string =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

/** Tỷ lệ % — làm tròn theo giá trị BE đã tính sẵn (không tự tính lại ở FE), "-" nếu null/undefined. */
export const formatPercent = (value?: number | null): string =>
  typeof value === 'number' ? `${value.toLocaleString('vi-VN')}%` : '-';

const pad2 = (n: number): string => n.toString().padStart(2, '0');

/** Ngày giờ dd/MM/yyyy HH:mm:ss — "-" nếu không có giá trị hoặc parse lỗi. */
export const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

/** Ngày dd/MM/yyyy — "-" nếu không có giá trị hoặc parse lỗi. */
export const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
};

/** Lấy payload items từ response PaginatedList<T> (Items/TotalItems/PageIndex/PageSize) — chấp
 * nhận cả camelCase (đã qua JSON serializer mặc định) lẫn field viết hoa đầu, phòng trường hợp
 * cấu hình serializer khác nhau giữa các API cũ/mới trong hệ thống. */
export const getPaginatedPayload = (response: any): { items: any[]; totalItems: number; totalPages: number } => {
  const payload = response?.data ?? response;
  const items = payload?.items ?? payload?.Items ?? [];
  const totalItems = payload?.totalItems ?? payload?.TotalItems ?? items.length;
  const totalPages = payload?.totalPages ?? payload?.TotalPages ?? Math.ceil(totalItems / (payload?.pageSize ?? payload?.PageSize ?? 1));
  return { items, totalItems, totalPages };
};

/** Loại bỏ param rỗng/undefined/null trước khi gọi API — xem README_RULE.md mục 1 (api_helper.ts
 * đã tự lọc ở tầng chung, nhưng vẫn giữ thói quen bọc cleanParams() để rõ ý định optional param). */
export const cleanParams = (params: Record<string, any>): Record<string, any> => {
  const cleaned: Record<string, any> = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};
